import { useState, useEffect, useCallback } from "react";
import {
    getWhatsappTemplates,
    createWhatsappTemplate,
    updateWhatsappTemplate,
    toggleWhatsappTemplate,
    deleteWhatsappTemplate,
} from "../../service/api/orders";
import TemplateForm from "../../components/TemplateForm";
import "../../css/WhatsappTemplates.css";

const TARGET_COLORS = {
    CUSTOMER: { bg: "#dbeafe", color: "#1e40af" },
    OWNER:    { bg: "#fef3c7", color: "#92400e" },
    STORE:    { bg: "#d1fae5", color: "#065f46" },
    DRIVER:   { bg: "#ede9fe", color: "#5b21b6" },
    ADMIN:    { bg: "#fee2e2", color: "#991b1b" },
    OTP:      { bg: "#fdf4ff", color: "#a21caf" },
    CANCEL:   { bg: "#fee2e2", color: "#991b1b" },
};
const statusColor = (t) => {
    if (t.startsWith("STATUS")) return { bg: "#f0fdf4", color: "#15803d" };
    return TARGET_COLORS[t] || { bg: "#f1f5f9", color: "#475569" };
};

export default function WhatsappTemplates() {
    const [templates, setTemplates]       = useState([]);
    const [targets, setTargets]           = useState([]);
    const [loading, setLoading]           = useState(true);
    const [filterTarget, setFilterTarget] = useState("");
    const [selected, setSelected]         = useState(null); // template being edited
    const [showForm, setShowForm]         = useState(false);
    const [toast, setToast]               = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getWhatsappTemplates(
                filterTarget ? { target: filterTarget } : {}
            );
            setTemplates(res?.templates);
            setTargets(res?.targets);
        } catch {
            showToast("Failed to load templates", "error");
        } finally {
            setLoading(false);
        }
    }, [filterTarget]);

    useEffect(() => { load(); }, [load]);

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleNew = () => {
        setSelected(null);
        setShowForm(true);
    };

    const handleEdit = (tpl) => {
        setSelected(tpl);
        setShowForm(true);
    };

    const handleToggle = async (tpl) => {
        try {
            await toggleWhatsappTemplate(tpl.id);
            setTemplates((prev) =>
                prev.map((t) => t.id === tpl.id ? { ...t, is_active: !t.is_active } : t)
            );
        } catch {
            showToast("Toggle failed", "error");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this template?")) return;
        try {
            await deleteWhatsappTemplate(id);
            setTemplates((prev) => prev.filter((t) => t.id !== id));
            if (selected?.id === id) setShowForm(false);
            showToast("Template deleted");
        } catch {
            showToast("Delete failed", "error");
        }
    };

    const handleSave = async (formData) => {
        try {
            if (selected) {
                const res = await updateWhatsappTemplate(selected.id, formData);
                setTemplates((prev) =>
                    prev.map((t) => t.id === selected.id ? res?.template : t)
                );
                showToast("Template updated");
            } else {
                const res = await createWhatsappTemplate(formData);
                setTemplates((prev) => [...prev, res?.template]);
                showToast("Template created");
            }
            setShowForm(false);
            setSelected(null);
        } catch (err) {
            const msg = err?.response?.data?.message || "Save failed";
            showToast(msg, "error");
        }
    };

    const stats = {
        total:    templates.length,
        active:   templates.filter((t) => t.is_active).length,
        inactive: templates.filter((t) => !t.is_active).length,
        types:    new Set(templates.map((t) => t.target)).size,
    };

    return (
        <div className="wt-page">
            {/* Page header */}
            <div className="wt-page-header">
                <div>
                    <h1 className="wt-page-title">
                        <span className="wt-wa-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                            </svg>
                        </span>
                        WhatsApp Templates
                    </h1>
                    <p className="wt-page-sub">Manage notification templates for customers, owners, drivers and admins</p>
                </div>
                <button className="wt-btn-primary" onClick={handleNew}>+ New Template</button>
            </div>

            {/* Stats */}
            <div className="wt-stats">
                {[
                    { label: "Total",    val: stats.total,    color: "#1e1e1e" },
                    { label: "Active",   val: stats.active,   color: "#10b981" },
                    { label: "Inactive", val: stats.inactive, color: "#ef4444" },
                    { label: "Types",    val: stats.types,    color: "#ff6b00" },
                ].map((s) => (
                    <div className="wt-stat-card" key={s.label}>
                        <div className="wt-stat-val" style={{ color: s.color }}>{s.val}</div>
                        <div className="wt-stat-lbl">{s.label}</div>
                    </div>
                ))}
            </div>

            <div className={`wt-body${showForm ? " wt-body--split" : ""}`}>
                {/* List */}
                <div className="wt-list-card">
                    <div className="wt-list-head">
                        <span className="wt-section-title">Templates</span>
                        <select
                            className="wt-select"
                            value={filterTarget}
                            onChange={(e) => setFilterTarget(e.target.value)}
                        >
                            <option value="">All targets</option>
                            {targets.map((t) => <option key={t}>{t}</option>)}
                        </select>
                    </div>

                    {loading ? (
                        <div className="wt-loading">
                            {[...Array(4)].map((_, i) => (
                                <div className="wt-skeleton" key={i} />
                            ))}
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="wt-empty">No templates found</div>
                    ) : (
                        <div className="wt-list">
                            {templates.map((tpl) => {
                                const c = statusColor(tpl.target);
                                return (
                                    <div
                                        key={tpl.id}
                                        className={`wt-item${selected?.id === tpl.id ? " wt-item--active" : ""}${!tpl.is_active ? " wt-item--inactive" : ""}`}
                                        onClick={() => handleEdit(tpl)}
                                    >
                                        <div className="wt-item-left">
                                            <div className="wt-item-name">{tpl.name}</div>
                                            <span className="wt-target-badge" style={{ background: c.bg, color: c.color }}>
                                                {tpl.target}
                                            </span>
                                            {tpl.reason && (
                                                <div className="wt-item-reason">{tpl.reason}</div>
                                            )}
                                            <div className="wt-item-body">{tpl.body.substring(0, 80)}…</div>
                                        </div>
                                        <div className="wt-item-right" onClick={(e) => e.stopPropagation()}>
                                            <label className="wt-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={tpl.is_active}
                                                    onChange={() => handleToggle(tpl)}
                                                />
                                                <span className="wt-toggle-track" />
                                                <span className="wt-toggle-thumb" />
                                            </label>
                                            <button
                                                className="wt-btn-delete"
                                                onClick={() => handleDelete(tpl.id)}
                                                title="Delete"
                                            >✕</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Form panel */}
                {showForm && (
                    <TemplateForm
                        initial={selected}
                        targets={targets}
                        onSave={handleSave}
                        onCancel={() => { setShowForm(false); setSelected(null); }}
                    />
                )}
            </div>

            {/* Toast */}
            {toast && (
                <div className={`wt-toast wt-toast--${toast.type}`}>{toast.msg}</div>
            )}
        </div>
    );
}